import { Room, getChatRoom, roomConverter } from '@breakroom-nx/classes';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  collection,
  query,
  startAt,
  endAt,
  getDocs,
  orderBy,
  where,
} from 'firebase/firestore';
import { firestore } from '@breakroom-nx/apis';

export const mapAPI = createApi({
  reducerPath: 'mapApi',
  keepUnusedDataFor: 3600,
  baseQuery: fakeBaseQuery<Error>(),
  tagTypes: ['MapChat'],
  endpoints: (builder) => ({
    getRoomsForRegion: builder.query<Room[], string[][] | undefined>({
      providesTags: ['MapChat'],
      async queryFn(bounds) {
        try {
          if (!bounds) {
            throw new Error('Bounds cannot be empty');
          }

          // console.info(`[MAP API]: Getting chats for region ${bounds}`);

          const chats: Room[] = [];
          const chatsRef = collection(firestore, 'chats');

          for (const b of bounds) {
            // console.log(`Bounds: ${b}`);

            const q = query(
              chatsRef,
              orderBy('geohash'),
              startAt(b[0]),
              endAt(b[1])
            ).withConverter(roomConverter);

            const querySnapshot = await getDocs(q);
            const promises = querySnapshot.docs.map(async (doc) => {
              const chatID = doc.id;
              await getChatRoom(chatID).then((room) => {
                if (room) {
                  chats.push(room);
                }
              });
            });

            await Promise.all(promises);
          }

          // console.info(`[MAP API]: Rooms found for region ${chats.length}`);

          return { data: chats };
        } catch (err) {
          // console.log(
          //   `[MAP API] (ERROR) Unable to get chats for region: ${bounds}`
          // );
          throw new Error('Unable to get rooms in the region at this time');
        }
      },
    }),

    /**
     * Will find chats whose document contains the corresponding vibe
     */
    findRoomsWithVibe: builder.query<Room[], { vibe: string }>({
      async queryFn({ vibe }) {
        try {
          if (!vibe) {
            return { error: 'Vibe cannot be empty' };
          }

          // console.info(`[MAP API]: Finding rooms for vibes: ${vibes}`);

          const chats: Room[] = [];
          const chatsRef = collection(firestore, 'chats');

          // Will find chats which match the vibe.
          const q = query(chatsRef, where('vibes', '==', vibe));
          // ! ERROR: - Literal Query. Will find chats whose DB array is exactly what is queried for. [Efficient]
          // const q = query(chatsRef, where('vibes', 'in', [vibes]));

          const querySnapshot = await getDocs(q);
          const promises = querySnapshot.docs.map(async (doc) => {
            // const firstQueryDoc = querySnapshot.docs[0];
            // const roomID = firstQueryDoc.id;
            // joinRoom(roomID, currentUser!.uid).then(() => {
            //   console.log('Room Joined');
            //   // Get Room Info -> Navigate User into Room
            //   getChatRoom(roomID).then((room) => {
            //     console.log('Got Data for Room: ', room);
            //     roomRetrieved = room;
            //   });
            // });

            const chatID = doc.id;
            await getChatRoom(chatID).then((room) => {
              if (room) {
                chats.push(room);
              }
            });
          });

          await Promise.all(promises);

          // console.info(`[MAP API]: Rooms found for vibes: ${chats.length}`);

          return { data: chats };
        } catch (err) {
          // console.log(
          //   `[MAP API] (ERROR) Unable to find rooms for selected vibes: ${vibes}`
          // );
          throw new Error('Error finding rooms with the selected vibe');
        }
      },
    }),
  }),
});

export const {
  useGetRoomsForRegionQuery,
  useFindRoomsWithVibeQuery,
  useLazyGetRoomsForRegionQuery,
} = mapAPI;
